<template>
  <transition name="alarm-flash-fade">
    <div v-if="visible" class="alarm-flash">
      <div class="alarm-flash__banner" @click="dismiss">
        <v-icon class="alarm-flash__icon">mdi-bell-ring</v-icon>
        <div class="alarm-flash__text">
          <span class="alarm-flash__title">{{ $t('alarm.new_alarm_flash') }}</span>
          <span v-if="alarmName" class="alarm-flash__alarm">{{ alarmName }}</span>
          <span v-if="locationLabel" class="alarm-flash__subtitle">{{ locationLabel }}</span>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { mapGetters } from 'vuex'

// Global, route-independent visual fallback that flashes when a new alarm
// appears. Works with zero audio/notification permission, so it always alerts
// an operator even when the browser blocks sound.
export default {
  name: 'AlarmFlashOverlay',
  data () {
    return {
      visible: false,
      alarm: { i18nKey: null, code: null, description: '' },
      propertyDescription: '',
      nucleusDescription: '',
      barnDescription: '',
      hideTimer: null
    }
  },
  computed: {
    ...mapGetters({ flash: 'activeAlarmFlash' }),
    // Human name of the alarm that just fired: SMAAI 5 i18n key, InobramX
    // translated description, or a code fallback.
    alarmName () {
      const alarm = this.alarm || {}
      if (alarm.i18nKey) {
        return this.$t('alarm.' + alarm.i18nKey)
      }
      if (alarm.description) {
        return alarm.description
      }
      if (alarm.code != null) {
        return `${this.$t('alarm.alarm')} ${alarm.code}`
      }
      return ''
    },
    // Propriedade › Núcleo › Galpão (only the parts that exist).
    locationLabel () {
      return [this.propertyDescription, this.nucleusDescription, this.barnDescription]
        .filter(Boolean)
        .join(' › ')
    }
  },
  watch: {
    'flash.tick' (tick) {
      if (!tick) {
        return
      }
      this.alarm = {
        i18nKey: this.flash.i18nKey,
        code: this.flash.code,
        description: this.flash.description
      }
      this.propertyDescription = this.flash.propertyDescription
      this.nucleusDescription = this.flash.nucleusDescription
      this.barnDescription = this.flash.barnDescription || this.flash.deviceDescription
      this.show()
    }
  },
  beforeDestroy () {
    this.clearTimer()
  },
  methods: {
    show () {
      this.visible = true
      this.clearTimer()
      this.hideTimer = setTimeout(() => {
        this.visible = false
      }, 6000)
    },
    dismiss () {
      this.visible = false
      this.clearTimer()
    },
    clearTimer () {
      if (this.hideTimer) {
        clearTimeout(this.hideTimer)
        this.hideTimer = null
      }
    }
  }
}
</script>

<style scoped>
  .alarm-flash {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 4000;
    pointer-events: none;
    display: flex;
    justify-content: center;
    align-items: flex-start;
    /* Pulsing red edge so the whole panel is visibly affected from across a room. */
    animation: alarm-flash-pulse 0.9s ease-in-out infinite;
    box-shadow: inset 0 0 0 8px #B3261E;
    background: transparent;
  }

  .alarm-flash__banner {
    margin-top: 24px;
    display: flex;
    align-items: center;
    background: #B3261E;
    color: #FCFCFD;
    border-radius: 12px;
    padding: 14px 22px;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.35);
    /* Only the banner is interactive; the rest of the overlay lets clicks pass
       through so it never blocks the operator's interaction with the UI. */
    pointer-events: auto;
    cursor: pointer;
  }

  .alarm-flash__icon {
    color: #FCFCFD !important;
    font-size: 32px;
    margin-right: 14px;
    animation: alarm-flash-shake 0.6s ease-in-out infinite;
  }

  .alarm-flash__text {
    display: flex;
    flex-direction: column;
  }

  .alarm-flash__title {
    font-size: 18px;
    font-weight: 700;
    line-height: 1.2;
  }

  .alarm-flash__alarm {
    font-size: 15px;
    font-weight: 600;
    line-height: 1.25;
  }

  .alarm-flash__subtitle {
    font-size: 14px;
    opacity: 0.9;
  }

  .alarm-flash-fade-enter-active,
  .alarm-flash-fade-leave-active {
    transition: opacity 0.25s ease;
  }

  .alarm-flash-fade-enter,
  .alarm-flash-fade-leave-to {
    opacity: 0;
  }

  @keyframes alarm-flash-pulse {
    0%, 100% {
      box-shadow: inset 0 0 0 8px rgba(179, 38, 30, 0.25);
    }
    50% {
      box-shadow: inset 0 0 0 16px rgba(179, 38, 30, 0.85);
    }
  }

  @keyframes alarm-flash-shake {
    0%, 100% {
      transform: rotate(-12deg);
    }
    50% {
      transform: rotate(12deg);
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/Alarm/AlarmFlashOverlay.vue